/*
 * Three independent pieces, all driven by the same "Shipping To: Yourself"
 * / "Shipping As Gift: Yes" line-item properties already set by the
 * product page's shipping-choice pills:
 *
 * 1. Gift message (.cart-gift-message-input) — one shared cart-level
 *    note, shown whenever there's at least one gift-shipped item. Saved
 *    as the cart's real `note` via /cart/update.js.
 *
 * 2. Structured gift-shipping form (.gift-shipping-input, rendered by
 *    snippets/gift-shipping-form.liquid) — recipient name/address/city/
 *    state/zip plus a Standard/Priority method choice, shown only
 *    when the cart has BOTH a self-shipped item AND a gift-shipped item
 *    ("mixed destination"). Saved as cart attributes via /cart/update.js.
 *    Picking a method adds the matching hidden fee product (real price,
 *    configured in Theme Settings via a product picker — see
 *    header.liquid / main-cart-items.liquid schemas) and removes the
 *    other one, so exactly one fee line ever exists at a time.
 *
 * 3. Checkout gating — while the form is incomplete, or the correct fee
 *    product isn't actually confirmed present in the cart, the checkout
 *    button(s) on both the cart page and the drawer are disabled, and
 *    clicking a blocked one shows inline errors and focuses the first
 *    incomplete field instead of navigating anywhere.
 *
 *    Honesty note: this reliably blocks the standard Checkout button
 *    and the cart form's normal submit, and best-effort visually/
 *    interactively disables the dynamic/express checkout buttons
 *    (Shop Pay, PayPal, etc. — rendered by Shopify's own script, not
 *    this file). None of this is tamper-proof against a technically
 *    determined customer (disabling JS, devtools). This file never
 *    claims otherwise, and validateCartState() below deliberately
 *    re-checks the REAL cart via a fresh fetch at the moment of
 *    checkout, not just the form's on-screen state, to close the
 *    window where a fee-product add is still in flight — see it for
 *    the actual authoritative check.
 *
 *    Modularity for a future server-side upgrade: every rule this file
 *    enforces (which fields are required, which fee variant must be
 *    present for which method) is isolated in validateField(),
 *    validateAll(), and validateCartState() below, all called before
 *    checkout in one place. A Cart & Checkout Validation Function added
 *    later would duplicate these same rules server-side as a second,
 *    authoritative layer — it wouldn't require removing or restructuring
 *    anything here; this file's client-side checks and a Function's
 *    server-side ones aren't mutually exclusive, they'd just both run.
 *
 * Uses event delegation on `document` throughout — both the cart page
 * and the drawer replace their inner HTML wholesale on every cart
 * update (Dawn's own cart.js does this), which would silently detach
 * any directly-bound listener.
 */
(function () {
  // The real signature inserted as actual editable text the moment the
  // gift-message textarea is clicked (see the focusin handler below).
  // The 2 leading "\n"s give exactly 2 blank rows (row 0, then one more)
  // before "From," lands on row 2 (the 3rd line) — leaving that much
  // room above the signature for the customer's own message.
  var GIFT_NOTE_DEFAULT = '\n\nFrom,\n{ Your Name Here }';

  var REQUIRED_FIELDS = ['name', 'address', 'city', 'state', 'zip'];
  // 'phone' is kept here on purpose even though gift-shipping-form.liquid
  // no longer has a Phone field at all — getFieldValue('phone') below
  // returns '' when nothing on the page matches [data-gift-field="phone"],
  // so every save still writes 'Gift Phone': '' to the cart. That's what
  // reliably clears out any value a customer's cart already had saved
  // from before this field was removed, rather than leaving it stuck.
  var ATTRIBUTE_KEY_BY_FIELD = {
    name: 'Gift Recipient Name',
    address: 'Gift Address',
    apt: 'Gift Apartment/Suite',
    city: 'Gift City',
    state: 'Gift State',
    zip: 'Gift Zip Code',
    phone: 'Gift Phone',
    method: 'Gift Shipping Method'
  };

  function config() {
    return window.giftShippingConfig || { enabled: false, standard: {}, priority: {} };
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* ---------- gift message (cart note) ---------- */

  /* Auto-grow: a plain <textarea> never expands with its own content —
     typing past the visible area just scrolls internally, hiding
     earlier lines exactly like the "compressing/hiding text" problem
     already fixed once for the default signature. Resetting height to
     'auto' first forces the browser to recompute scrollHeight against
     the CURRENT content only (otherwise a previous larger height would
     never let scrollHeight shrink back down after deleting text);
     setting height to that scrollHeight then grows the box to fit
     everything typed, with no scrollbar and nothing ever hidden.
     min-height (CSS) still guarantees the floor — the signature-sized
     box — this only ever grows it taller, never shorter than that. */
  function autoGrowGiftMessage(textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  function autoGrowAllGiftMessages() {
    document.querySelectorAll('.cart-gift-message-input').forEach(autoGrowGiftMessage);
  }

  /* .cart-gift-message-clear lives as a sibling of the textarea inside
     the same .cart__gift-message wrapper (not inside the textarea
     itself — a <textarea> can't contain child elements), so this walks
     up to that shared wrapper the same way saveGiftMessage's own
     statusEl lookup does. */
  function giftMessageClearButton(textarea) {
    var messageContainer = textarea.closest('.cart__gift-message');
    return messageContainer && messageContainer.querySelector('.cart-gift-message-clear');
  }

  /* Shows the Clear pill exactly while there's real text to clear —
     covers all three ways that can become true: typing, the default
     signature landing as real text the moment the box is clicked, and
     a genuinely saved note already present on page load. Empty value
     (untouched, or reverted by the focusout handler below) hides it
     again.
     Sets the inline style directly rather than toggling a class whose
     effect depends on a stylesheet rule (component-gift-shipping.css's
     .cart-gift-message-clear.is-visible) beating whatever the theme's
     own global button styling happens to specify for display — an
     inline style always wins over an external stylesheet rule unless
     that rule uses !important, so this is the more reliable of the
     two regardless of what else on the page targets <button>. */
  function updateGiftMessageClearButton(textarea) {
    var button = giftMessageClearButton(textarea);
    if (!button) return;
    var visible = textarea.value !== '';
    // 'inline-block', not 'block' — the button's parent row
    // (.cart-gift-message-clear-row) right-aligns it with plain
    // text-align, which only affects inline-level content.
    button.style.display = visible ? 'inline-block' : 'none';
    button.classList.toggle('is-visible', visible); // kept in sync too, in case anything else keys off it
  }

  function updateAllGiftMessageClearButtons() {
    document.querySelectorAll('.cart-gift-message-input').forEach(updateGiftMessageClearButton);
  }

  /* The textarea's own placeholder (Liquid: "Enjoy your Gift!" + a blank
     line + "From, / { Your Name Here }") only ever shows while the
     value is genuinely empty — the instant real content exists, native
     placeholder behavior hides it, signature lines included, right
     away. So on the first click/tab-into an empty box, this inserts the
     real (longer) GIFT_NOTE_DEFAULT as actual value immediately —
     "Enjoy your Gift!" disappears the moment you click in, exactly like
     any other placeholder does the instant a real value exists.
     Delegated via focusin (bubbles, unlike plain focus) for the same
     AJAX-re-render reason as everything else in this file — a directly-
     bound listener would be lost the moment Dawn's cart.js replaces
     this markup. */
  document.addEventListener('focusin', function (event) {
    var textarea = event.target.closest && event.target.closest('.cart-gift-message-input');
    if (!textarea) return;
    if (textarea.dataset.skipDefaultInsert) {
      // Set by the Clear button's click handler below, right before a
      // refocus it triggers itself — that's a "start a fresh message"
      // action, not a customer clicking into a genuinely untouched box,
      // so this one time skips re-inserting the default signature.
      delete textarea.dataset.skipDefaultInsert;
    } else if (textarea.value === '') {
      textarea.value = GIFT_NOTE_DEFAULT;
      autoGrowGiftMessage(textarea);
    }
    updateGiftMessageClearButton(textarea); // Clear pill appears the moment there's real text
  });

  /* Mirror of the focusin handler above: clicking in and back out again
     without ever typing anything real should NOT count as writing a
     gift note — the box should settle back to exactly how it looked
     before it was clicked (placeholder showing, nothing saved), not be
     left holding the bare, unedited signature as if that were an
     actual message. Only a genuine keystroke ever fires this file's
     'input' handler (a programmatic .value assignment, like the
     focusin insertion above, never does), so comparing against the
     exact GIFT_NOTE_DEFAULT string here reliably tells "never touched"
     apart from "typed something, even one character, beyond the
     signature" — the customer's own extra text always changes this. */
  document.addEventListener('focusout', function (event) {
    var textarea = event.target.closest && event.target.closest('.cart-gift-message-input');
    if (!textarea) return;
    if (textarea.value !== GIFT_NOTE_DEFAULT) return; // real content (or already empty) — leave it alone
    textarea.value = '';
    autoGrowGiftMessage(textarea);
    updateGiftMessageClearButton(textarea); // back to empty — hide the Clear pill again
    // Also pushes the clear to the server — covers the case where the
    // customer HAD typed something, saved it, then deleted it all back
    // down to exactly the bare signature before clicking out. Without
    // this, cart.note would stay saved as that bare signature forever
    // even though the box now shows nothing but the placeholder again.
    // In the far more common "never typed anything" case this just
    // saves an empty note over an already-empty one — harmless.
    saveGiftMessage(textarea);
  });

  function saveGiftMessage(textarea) {
    // .closest('.cart__gift-message'), not .parentElement — walks up
    // to the shared field wrapper regardless of exactly how deep the
    // textarea is nested, so .cart-gift-message-status (a sibling of
    // the textarea inside that same wrapper) is always found.
    var messageContainer = textarea.closest('.cart__gift-message');
    var statusEl = messageContainer && messageContainer.querySelector('.cart-gift-message-status');
    if (statusEl) statusEl.textContent = 'Saving…';

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: textarea.value })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update request failed');
        return response.json();
      })
      .then(function () {
        if (statusEl) {
          statusEl.textContent = 'Saved';
          setTimeout(function () {
            if (statusEl) statusEl.textContent = '';
          }, 2000);
        }
        document.querySelectorAll('.cart-gift-message-input').forEach(function (other) {
          if (other === textarea) return;
          other.value = textarea.value;
          autoGrowGiftMessage(other);
          updateGiftMessageClearButton(other);
        });
      })
      .catch(function () {
        if (statusEl) statusEl.textContent = 'Could not save — please try again';
      });
  }

  /* ---------- Occasion dropdown (cart attribute, isolated from the
     mixed-destination gift-shipping form's own field-tracking/validation
     below — it's just a single always-optional attribute, saved the
     same simple way as the gift message) ---------- */

  /* ---------- Occasion dropdown "+" rotation ----------
     Native <select> has no real open/close event, so this approximates
     it: focusin (clicking/tabbing into the box) rotates the + into an
     ×, focusout or a completed selection rotates it back. Delegated via
     focusin/focusout (these bubble, unlike plain focus/blur) for the
     same AJAX-re-render reason as everything else in this file. */
  document.addEventListener('focusin', function (event) {
    var select = event.target.closest && event.target.closest('.cart-occasion-select');
    if (!select) return;
    var wrapper = select.closest('.cart-occasion-dropdown__box');
    if (wrapper) wrapper.classList.add('is-open');
  });
  document.addEventListener('focusout', function (event) {
    var select = event.target.closest && event.target.closest('.cart-occasion-select');
    if (!select) return;
    var wrapper = select.closest('.cart-occasion-dropdown__box');
    if (wrapper) wrapper.classList.remove('is-open');
  });

  function saveOccasion(select) {
    var wrapper = select.closest('.cart-occasion-dropdown__box');
    if (wrapper) wrapper.classList.remove('is-open');

    var statusEl = select.closest('.cart-occasion-dropdown') && select.closest('.cart-occasion-dropdown').querySelector('.cart-occasion-status');
    if (statusEl) statusEl.textContent = 'Saving…';

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Occasion: select.value } })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update request failed');
        return response.json();
      })
      .then(function () {
        if (statusEl) {
          statusEl.textContent = 'Saved';
          setTimeout(function () {
            if (statusEl) statusEl.textContent = '';
          }, 2000);
        }
        document.querySelectorAll('.cart-occasion-select').forEach(function (other) {
          if (other !== select) other.value = select.value;
        });
      })
      .catch(function () {
        if (statusEl) statusEl.textContent = 'Could not save — please try again';
      });
  }

  /* ---------- structured gift-shipping form ---------- */

  function allFormRoots() {
    return document.querySelectorAll('[data-gift-shipping-root]');
  }

  function fieldElements(fieldName) {
    // Both surfaces (page + drawer) can be in the DOM at once, so this
    // can return up to 2 elements for any given field.
    return document.querySelectorAll('[data-gift-field="' + fieldName + '"]');
  }

  function getFieldValue(fieldName) {
    var els = fieldElements(fieldName);
    if (!els.length) return '';
    if (fieldName === 'method') {
      var checked = document.querySelector('[data-gift-field="method"]:checked');
      return checked ? checked.value : '';
    }
    return els[0].value.trim();
  }

  function syncFieldAcrossInstances(changedEl, fieldName, value) {
    fieldElements(fieldName).forEach(function (el) {
      if (el === changedEl) return;
      if (fieldName === 'method') {
        el.checked = el.value === value;
      } else {
        el.value = value;
      }
    });
  }

  function validateField(fieldName) {
    var value = getFieldValue(fieldName);
    if (fieldName === 'method') {
      if (!config().enabled) return null; // not required when fee feature is off
      return value ? null : 'Please select a Gift Shipping Method.';
    }
    if (REQUIRED_FIELDS.indexOf(fieldName) === -1) return null; // apt/phone are optional
    if (!value) {
      var emptyMessages = {
        name: 'Please enter a Recipient Name.',
        address: 'Please enter a valid Address.',
        city: 'Please enter a valid City.',
        state: 'Please enter a valid State.',
        zip: 'Please enter a valid ZIP code.'
      };
      return emptyMessages[fieldName] || 'Please fill out this field.';
    }
    if (fieldName === 'zip' && !/^\d{5}(-\d{4})?$/.test(value)) {
      return 'Please enter a valid ZIP code.';
    }
    if (fieldName === 'address' && (value.trim().length < 5 || !/\s/.test(value.trim()))) {
      // Requires some minimum length and more than one "word" — catches
      // a bare number with nothing after it (e.g. "40" alone), which
      // is the specific gap that let incomplete addresses through
      // silently before. Deliberately NOT requiring it to start with a
      // digit — that would incorrectly reject legitimate formats like
      // "PO Box 123."
      return 'Please enter a valid Street Address.';
    }
    if (fieldName === 'city' && !/^[A-Za-z\s'.-]{2,}$/.test(value)) {
      // City names really can't be reliably validated beyond format —
      // this catches digits/symbols and single characters, but can't
      // tell a real short city apart from letters typed at random
      // (like "asd") since both are just a few letters. Genuinely
      // verifying a real city needs an address-lookup service (Google
      // Places, Shopify's own address autocomplete, etc.), which is a
      // meaningfully bigger addition than a format check — let me know
      // if that's worth doing.
      return 'Please enter a valid City.';
    }
    if (fieldName === 'name' && !/^\S+\s+\S+/.test(value)) {
      // Requires a first AND last name (two words) — easy to add, as
      // you said, though same caveat as city: can't verify these are
      // real names, just that there are two of them.
      return 'Please enter a First and Last Name.';
    }
    return null;
  }

  function validateAll() {
    var fields = REQUIRED_FIELDS.concat(['method']);
    var errors = {};
    fields.forEach(function (fieldName) {
      var message = validateField(fieldName);
      if (message) errors[fieldName] = message;
    });
    return errors;
  }

  function isMixedDestinationActiveOnPage() {
    return allFormRoots().length > 0;
  }

  var errorIdCounter = 0;

  // Only ever touches error styling/text and ARIA attributes, never a
  // field's value: validation must not reset anything the customer
  // entered or selected.
  function renderFieldError(fieldName, message) {
    document.querySelectorAll('[data-gift-field-wrap="' + fieldName + '"]').forEach(function (wrap) {
      if (message) {
        wrap.classList.add('has-error');
      } else {
        wrap.classList.remove('has-error');
      }
      var errorEl = wrap.querySelector('[data-gift-error="' + fieldName + '"]');
      if (errorEl) {
        errorEl.textContent = message || '';
        if (!errorEl.id) errorEl.id = 'GiftFieldError-' + fieldName + '-' + ++errorIdCounter;
      }
      wrap.querySelectorAll('[data-gift-field]').forEach(function (control) {
        if (message) {
          control.setAttribute('aria-invalid', 'true');
          if (errorEl) control.setAttribute('aria-describedby', errorEl.id);
        } else {
          control.removeAttribute('aria-invalid');
          if (errorEl && control.getAttribute('aria-describedby') === errorEl.id) {
            control.removeAttribute('aria-describedby');
          }
        }
      });
    });
  }

  function renderAllErrors(errors) {
    REQUIRED_FIELDS.concat(['method']).forEach(function (fieldName) {
      renderFieldError(fieldName, errors[fieldName]);
    });
  }

  function focusFirstInvalid(errors) {
    var order = ['name', 'address', 'city', 'state', 'zip', 'method'];
    for (var i = 0; i < order.length; i++) {
      if (errors[order[i]]) {
        // Page and drawer can both be in the DOM; use the instance the
        // customer can actually see (skips a closed drawer's copy).
        var el = Array.prototype.find.call(
          document.querySelectorAll('[data-gift-field="' + order[i] + '"]'),
          function (candidate) { return candidate.getClientRects().length > 0; }
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus({ preventScroll: true });
        }
        return;
      }
    }
  }

  function collectAttributes() {
    var attributes = {};
    Object.keys(ATTRIBUTE_KEY_BY_FIELD).forEach(function (fieldName) {
      attributes[ATTRIBUTE_KEY_BY_FIELD[fieldName]] = getFieldValue(fieldName);
    });
    return attributes;
  }

  function saveGiftShippingForm() {
    if (!isMixedDestinationActiveOnPage()) return Promise.resolve();

    var statusEls = document.querySelectorAll('[data-gift-save-status]');
    statusEls.forEach(function (el) {
      el.textContent = 'Saving…';
    });

    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: collectAttributes() })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update request failed');
        return response.json();
      })
      .then(function () {
        statusEls.forEach(function (el) {
          el.textContent = 'Saved';
          setTimeout(function () {
            el.textContent = '';
          }, 2000);
        });
        updateCheckoutGatingFromLiveForm();
      })
      .catch(function () {
        statusEls.forEach(function (el) {
          el.textContent = 'Could not save — please try again';
        });
      });
  }

  var debouncedSaveForm = debounce(saveGiftShippingForm, 700);

  /* ---------- fee-product reconciliation ---------- */

  var isReconciling = false;
  var reconcileQueuedAgain = false;

  function findFeeLine(cartData, variantId) {
    if (!variantId) return null;
    for (var i = 0; i < cartData.items.length; i++) {
      if (String(cartData.items[i].variant_id) === String(variantId)) {
        return { item: cartData.items[i], line: i + 1 };
      }
    }
    return null;
  }

  function removeLine(line) {
    return fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    });
  }

  function addFeeVariant(variantId) {
    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
  }

  // Cheap, synchronous, no-network check. Scoped to just the cart items
  // container (not the whole page — document.body.innerText would force
  // a full-page layout recalc and be slow in its own right) so this
  // costs virtually nothing to run on every single cart update. If
  // nothing here suggests gift-shipping is or was relevant, the full
  // reconcile (which does at least one /cart.js fetch, sometimes
  // several more) gets skipped entirely — this is what was making
  // ordinary quantity/delete actions slow on carts that have nothing
  // to do with gift shipping at all.
  function cartMightNeedReconciliation() {
    if (document.querySelector('[data-gift-shipping-root], .cart__gift-message')) return true;
    var itemsContainer = document.querySelector('.cart-items, cart-drawer-items, #main-cart-items');
    if (!itemsContainer) return true; // can't tell — be safe and check properly
    var text = itemsContainer.textContent || '';
    return text.indexOf('Shipping To:') !== -1 || text.indexOf('Shipping As Gift:') !== -1;
  }

  function refreshCartTotals() {
    // Fetches ONLY main-cart-footer's rendered HTML and swaps in just
    // its .js-contents element — confirmed by reading
    // main-cart-footer.liquid directly that this is the ONLY
    // .js-contents in that whole file, and it belongs exclusively to
    // the "subtotal" block (which also holds the gift-only note, see
    // that file). The "buttons" block (checkout button, PayPal/Shop
    // Pay/Apple Pay) has no .js-contents of its own at all, so it's
    // structurally impossible for this to touch it — nothing here
    // replaces or re-executes any of those third-party <script> tags.
    var footer = document.getElementById('main-cart-footer');
    if (!footer) return; // drawer-only page state, or footer not present
    var target = footer.querySelector('.js-contents');
    if (!target) return;

    // footer.dataset.id is the section's true instance ID (e.g.
    // "template--21790876467288__cart-footer"), set by Liquid via
    // data-id="{{ section.id }}". The Section Rendering API needs this
    // exact value to include block data — passing the template
    // filename ("main-cart-footer") instead still returns 200 OK, but
    // with section.blocks completely empty, since block configuration
    // is tied to the specific instance in cart.json, not the generic
    // file. That was the actual bug: the totals genuinely never
    // updated because this fetch was silently returning nothing to
    // apply, not because of any timing/race issue.
    var sectionId = footer.dataset.id;
    if (!sectionId) return;

    fetch(window.routes.cart_url + '?section_id=' + sectionId)
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var source = html.querySelector('.js-contents');
        if (source) target.innerHTML = source.innerHTML;
      })
      .catch(function () {});
  }

  function reconcileFeeProduct(providedCartData) {
    if (!cartMightNeedReconciliation()) return;
    if (isReconciling) {
      reconcileQueuedAgain = true;
      return;
    }
    isReconciling = true;

    var mutationOccurred = false;

    // If the caller already has fresh cart data (Dawn's own quantity/
    // remove updates publish it as part of the cartUpdate event — same
    // exact shape as a /cart.js response), use that directly instead of
    // fetching again. This was the biggest remaining source of the
    // slowdown: every single quantity change or removal was triggering
    // a completely redundant extra network round-trip here, even
    // though the data it fetched was already sitting in the event that
    // triggered this function in the first place.
    var cartDataSource =
      providedCartData && providedCartData.items
        ? Promise.resolve(providedCartData)
        : fetch(window.routes.cart_url + '.js').then(function (response) {
            return response.json();
          });

    cartDataSource
      .then(function (cartData) {
        var hasSelf = false;
        var hasGift = false;
        var hasGiftBoxItem = false;
        cartData.items.forEach(function (item) {
          var props = item.properties || {};
          if (props['Shipping To'] === 'Yourself') hasSelf = true;
          if (props['Shipping As Gift'] === 'Yes') hasGift = true;
          // product_type is the cart JSON's name for what Liquid reads as
          // item.product.type — same field, just under its /cart.js key.
          if (item.product_type === 'Gift Box') hasGiftBoxItem = true;
        });
        var mixedActive = hasSelf && hasGift;
        var cfg = config();

        var standardLine = findFeeLine(cartData, cfg.standard && cfg.standard.variantId);
        var priorityLine = findFeeLine(cartData, cfg.priority && cfg.priority.variantId);

        var selectedMethod = mixedActive ? cartData.attributes['Gift Shipping Method'] : null;
        var work = Promise.resolve();

        if (!mixedActive || !cfg.enabled) {
          // Condition no longer met (or feature turned off) — remove
          // whichever fee line(s) exist. Never both should exist at
          // once, but check both defensively.
          if (standardLine) {
            mutationOccurred = true;
            work = work.then(function () { return removeLine(standardLine.line); });
          }
          if (priorityLine) {
            mutationOccurred = true;
            work = work.then(function () { return removeLine(priorityLine.line); });
          }
          // Also clear the method selection itself (and the address
          // fields) once the mixed condition is fully gone — cart
          // attributes otherwise persist indefinitely, so without this
          // a "Standard" pick from a much earlier, completely
          // unrelated order would silently show pre-selected the next
          // time ANY new mixed-destination cart happens to form, with
          // no fee line to back it up since nothing had run yet to
          // reconcile it. Note (cleared separately, see below) has its
          // own condition since a gift-only cart still legitimately
          // uses it; these fields don't apply outside mixed at all.
          if (!mixedActive && cartData.attributes && cartData.attributes['Gift Shipping Method']) {
            mutationOccurred = true;
            work = work.then(function () {
              var clearedAttributes = {};
              Object.keys(ATTRIBUTE_KEY_BY_FIELD).forEach(function (fieldName) {
                clearedAttributes[ATTRIBUTE_KEY_BY_FIELD[fieldName]] = '';
              });
              return fetch(window.routes.cart_update_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ attributes: clearedAttributes })
              });
            });
          }
        } else {
          var wantStandard = selectedMethod === 'Standard';
          var wantPriority = selectedMethod === 'Priority';

          if (!wantStandard && standardLine) {
            mutationOccurred = true;
            work = work.then(function () { return removeLine(standardLine.line); });
          }
          if (!wantPriority && priorityLine) {
            mutationOccurred = true;
            work = work.then(function () { return removeLine(priorityLine.line); });
          }
          if (wantStandard && !standardLine && cfg.standard.variantId) {
            mutationOccurred = true;
            work = work.then(function () { return addFeeVariant(cfg.standard.variantId); });
          }
          if (wantPriority && !priorityLine && cfg.priority.variantId) {
            mutationOccurred = true;
            work = work.then(function () { return addFeeVariant(cfg.priority.variantId); });
          }
          // Guard against a duplicate if one somehow already exists at
          // qty > 1 (e.g. a customer manually editing quantity via
          // devtools/API) — normal UI never exposes a way to do this,
          // since the fee line isn't user-quantity-editable, but this
          // keeps it to exactly 1 regardless.
          if (wantStandard && standardLine && standardLine.item.quantity !== 1) {
            mutationOccurred = true;
            work = work.then(function () {
              return fetch(window.routes.cart_change_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ line: standardLine.line, quantity: 1 })
              });
            });
          }
          if (wantPriority && priorityLine && priorityLine.item.quantity !== 1) {
            mutationOccurred = true;
            work = work.then(function () {
              return fetch(window.routes.cart_change_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ line: priorityLine.line, quantity: 1 })
              });
            });
          }
        }

        // Gift Note clearing: only when there's no gift item left in the
        // cart at all (self-only, or empty) — NOT merely when leaving
        // "mixed", since a gift-only cart has its own legitimate note
        // field now (see main-cart-footer.liquid / cart-drawer.liquid)
        // and should keep whatever the customer already typed.
        if (!hasGift && cartData.note) {
          mutationOccurred = true;
          work = work.then(function () {
            return fetch(window.routes.cart_update_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ note: '' })
            });
          });
        }

        // Gift Occasion clearing: the "attributes[Occasion]" dropdown
        // (main-cart-footer.liquid / gift-shipping-form.liquid) is a
        // cart-level attribute, not a line-item property, so Shopify
        // never clears it on its own when the Gift Box item it applies
        // to is removed — it would otherwise silently linger and show
        // pre-selected the next time ANY Gift Box item is added, even
        // in a completely unrelated later cart. Clear it here the same
        // way the fee-line/Gift-Shipping-Method attributes above already
        // get cleared once their condition is gone, so it only ever
        // stays selected while its correlating Gift Box item is still
        // actually in the cart.
        if (!hasGiftBoxItem && cartData.attributes && cartData.attributes.Occasion) {
          mutationOccurred = true;
          work = work.then(function () {
            return fetch(window.routes.cart_update_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: { Occasion: '' } })
            });
          });
        }

        return work;
      })
      .then(function () {
        // Only refresh/re-render if something actually changed — a
        // total no-op reconcile pass does neither of these.
        if (!mutationOccurred) return;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-shipping' });
        }
        // Dawn's own onCartUpdate() (triggered by the publish() above)
        // only ever fetches section_id=main-cart-items — confirmed by
        // reading cart.js directly. It never touches main-cart-footer,
        // which is where the totals live, so the fee line itself
        // updates correctly but the price never did without this.
        refreshCartTotals();
        updateCheckoutGatingFromLiveForm();
      })
      .catch(function () {})
      .finally(function () {
        isReconciling = false;
        if (reconcileQueuedAgain) {
          reconcileQueuedAgain = false;
          reconcileFeeProduct();
        }
      });
  }

  /* ---------- checkout gating ---------- */

  function checkoutButtons() {
    return document.querySelectorAll('[data-gift-shipping-checkout-button]');
  }

  function dynamicCheckoutContainers() {
    return document.querySelectorAll('[data-gift-shipping-dynamic-buttons]');
  }

  /* ---------- "pressed while grayed out" prompt ----------
     A disabled checkout button (and the inert/pointer-events:none
     express-checkout container) never receives clicks at all, so the
     press lands on whatever sits underneath it instead. The document
     click listener further down checks whether a click landed inside a
     BLOCKED button's box and, if so, calls showBlockedCheckoutPrompt():
     a red "Please fill out all required fields.*" line appears directly
     above each checkout button, and every empty/invalid required field
     (including both Gift Shipping Method options) is highlighted red
     with its own message, exactly as if it had been clicked into and
     left blank. Fields clear back to normal as they're filled in (the
     existing input/change/blur handlers), and the line above the
     button disappears once nothing is blocking checkout anymore. The
     gray/disabled behavior itself is unchanged. */
  var GATE_MESSAGE = '*Please fill out all required fields.';

  function removeGateMessages() {
    document.querySelectorAll('[data-gift-gate-message]').forEach(function (el) {
      el.remove();
    });
    checkoutButtons().forEach(function (button) {
      var describedBy = button.getAttribute('aria-describedby') || '';
      if (describedBy.indexOf('GiftGateError-') === 0) button.removeAttribute('aria-describedby');
    });
  }

  function showGateMessages() {
    checkoutButtons().forEach(function (button) {
      // Above the button's CTA group (not inside it — Dawn lays
      // .cart__ctas out as a flex row, which would put the message
      // beside the button instead of above it).
      var anchor = button.closest('.cart__ctas') || button;
      var prev = anchor.previousElementSibling;
      if (prev && prev.hasAttribute('data-gift-gate-message')) return;
      var message = document.createElement('p');
      message.className = 'gift-shipping__gate-error';
      message.id = 'GiftGateError-' + ++errorIdCounter;
      message.setAttribute('data-gift-gate-message', '');
      message.setAttribute('role', 'alert');
      message.setAttribute('aria-live', 'assertive');
      message.textContent = GATE_MESSAGE;
      anchor.parentNode.insertBefore(message, anchor);
      button.setAttribute('aria-describedby', message.id);
    });
  }

  function showBlockedCheckoutPrompt() {
    if (!isMixedDestinationActiveOnPage()) return;
    var errors = validateAll();
    if (Object.keys(errors).length === 0) {
      // Nothing actually missing on screen: re-sync the gating state
      // instead of showing a misleading prompt.
      updateCheckoutGatingFromLiveForm();
      return;
    }
    renderAllErrors(errors);
    showGateMessages();
    focusFirstInvalid(errors);
  }

  function pointInside(el, x, y) {
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false; // hidden (e.g. closed drawer)
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  function blockedCheckoutAt(x, y) {
    var candidates = document.querySelectorAll(
      '[data-gift-shipping-checkout-button][data-gift-shipping-blocked], [data-gift-shipping-dynamic-buttons][data-gift-shipping-blocked]'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (pointInside(candidates[i], x, y)) return candidates[i];
    }
    return null;
  }

  function updateCheckoutGatingFromLiveForm() {
    // Previously returned early here doing nothing at all once the
    // mixed-destination form disappeared (e.g. after deleting the item
    // that made it stop applying) — which left the checkout button
    // stuck disabled forever if it happened to be blocked right before
    // that deletion. Now explicitly resolves to "not blocked" in that
    // case instead of leaving it stale.
    var blocked = false;
    if (isMixedDestinationActiveOnPage()) {
      var errors = validateAll();
      blocked = Object.keys(errors).length > 0;
    }

    if (!blocked) removeGateMessages();

    // The checkout button LOOKS disabled while blocked (aria-disabled +
    // data-gift-shipping-blocked, grayed in component-gift-shipping.css)
    // but stays a real, focusable, clickable button, so a click, tap,
    // Enter or Space on it runs validation (the click handler below
    // always intercepts it while the form is incomplete) instead of
    // doing nothing. Once everything is valid it's fully enabled and
    // proceeds normally. The Liquid markup still renders it with
    // `disabled` as a no-JavaScript safety net; this converts it.
    checkoutButtons().forEach(function (button) {
      button.disabled = false;
      if (blocked) {
        button.setAttribute('data-gift-shipping-blocked', '');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('data-gift-shipping-blocked');
        button.removeAttribute('aria-disabled');
      }
    });
    dynamicCheckoutContainers().forEach(function (container) {
      if (blocked) {
        container.setAttribute('data-gift-shipping-blocked', '');
        // inert (where supported) removes the express-checkout buttons
        // from focus, click, and assistive-tech interaction entirely —
        // stronger than the CSS pointer-events:none fallback alone,
        // which a keyboard user could in principle still tab past into.
        // Best-effort, same as everything else here: these buttons are
        // rendered and wired up by Shopify's own script, not this file.
        container.inert = true;
      } else {
        container.removeAttribute('data-gift-shipping-blocked');
        container.inert = false;
      }
    });
  }

  /* ---------- event wiring ---------- */

  document.addEventListener(
    'input',
    function (event) {
      var occasionField = event.target.closest && event.target.closest('.cart-occasion-select');
      if (occasionField) {
        // Same reasoning as the gift-message stopPropagation just below —
        // some browsers fire 'input' on <select> too, and it must never
        // reach Dawn's <cart-items> quantity-validator.
        event.stopPropagation();
        return;
      }

      var messageField = event.target.closest && event.target.closest('.cart-gift-message-input');
      if (messageField) {
        // Stops this from ever reaching Dawn's <cart-items> element,
        // which listens for ANY 'change'/'input' event with zero type
        // checking at all — validateQuantity() in cart.js blindly
        // assumes event.target is a quantity input (reads .value,
        // .dataset.min, .max, .step without checking what element it
        // actually is), which is what was producing the bogus
        // "increments of [undefined]" error on completely unrelated
        // fields once this form started living inside the same DOM
        // subtree as the cart items table. Capture phase (true below)
        // is required for this to actually work — <cart-items>'s own
        // listener is bubble-phase and sits BETWEEN this field and
        // document, so it fires before a bubble-phase listener on
        // document ever would.
        event.stopPropagation();
        // Grows immediately on every keystroke (not debounced like the
        // save below) — the box should never visibly compress/scroll
        // while typing, even for the ~700ms before the save fires.
        autoGrowGiftMessage(messageField);
        // Covers a manual select-all-and-delete back down to nothing
        // while still focused (the focusin/focusout handlers above only
        // fire once per focus, so they wouldn't catch this) — the Clear
        // pill should disappear the instant there's genuinely no text
        // left, same as it appears the instant there is any.
        updateGiftMessageClearButton(messageField);
        debounce(saveGiftMessage, 700)(messageField);
        return;
      }

      var giftField = event.target.closest && event.target.closest('[data-gift-field]');
      if (!giftField) return;
      event.stopPropagation();

      var fieldName = giftField.dataset.giftField;
      syncFieldAcrossInstances(giftField, fieldName, giftField.value);
      // Real-time: a field that's already showing an error is re-checked
      // on every keystroke and clears the moment it becomes valid (no
      // new checkout attempt needed). Fields not yet flagged aren't
      // nagged while the customer is still typing; blur checks those.
      var wrap = giftField.closest('[data-gift-field-wrap]');
      if (wrap && wrap.classList.contains('has-error')) {
        renderFieldError(fieldName, validateField(fieldName));
      }
      updateCheckoutGatingFromLiveForm();
      debouncedSaveForm();
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var occasionField = event.target.closest && event.target.closest('.cart-occasion-select');
      if (occasionField) {
        event.stopPropagation();
        saveOccasion(occasionField);
        return;
      }

      var messageField = event.target.closest && event.target.closest('.cart-gift-message-input');
      if (messageField) {
        event.stopPropagation();
        return;
      }

      var giftField = event.target.closest && event.target.closest('[data-gift-field]');
      if (!giftField) return;
      event.stopPropagation();

      var fieldName = giftField.dataset.giftField;

      if (fieldName === 'method') {
        var value = giftField.checked ? giftField.value : getFieldValue('method');
        syncFieldAcrossInstances(giftField, 'method', value);
        renderFieldError('method', null);
        updateCheckoutGatingFromLiveForm();
        // Method changes are a discrete choice, not free-text typing —
        // save + reconcile the fee line immediately rather than waiting
        // out the debounce. Chained (not fired concurrently) —
        // reconcileFeeProduct() reads the "Gift Shipping Method"
        // attribute fresh from /cart.js, so it has to run AFTER the
        // save actually lands, not alongside it. Firing both at once
        // was a real race: reconcile could read the cart before
        // Shopify had recorded the new attribute, see the OLD method
        // (or none), and skip adding the fee product on the first
        // click — only correcting itself on a LATER, unrelated
        // reconcile pass.
        saveGiftShippingForm().then(function () {
          reconcileFeeProduct();
        });
      }

      if (fieldName === 'state') {
        syncFieldAcrossInstances(giftField, 'state', giftField.value);
        renderFieldError('state', validateField('state'));
        updateCheckoutGatingFromLiveForm();
        debouncedSaveForm();
      }
    },
    true
  );

  document.addEventListener(
    'blur',
    function (event) {
      var messageField = event.target.closest && event.target.closest('.cart-gift-message-input');
      if (messageField) {
        saveGiftMessage(messageField);
        return;
      }
      var giftField = event.target.closest && event.target.closest('[data-gift-field]');
      if (!giftField) return;
      var fieldName = giftField.dataset.giftField;
      var message = validateField(fieldName);
      renderFieldError(fieldName, message);
      saveGiftShippingForm();
    },
    true
  );

  // The authoritative check — re-fetches the real cart and confirms the
  // fee line that should exist for the selected method actually does,
  // at the exact moment of checkout. The form-state checks elsewhere in
  // this file (validateAll) are a fast on-screen preview for immediate
  // feedback as the customer types; this is what actually gates
  // checkout, since reconcileFeeProduct()'s /cart/add.js call is
  // asynchronous — a customer could click Checkout in the brief window
  // after picking a method but before that request finishes, and the
  // form would still *look* valid (the radio is checked) even though
  // the fee line isn't in the cart yet. This closes that window by
  // never trusting on-screen state alone for the actual gate.
  function validateCartState() {
    return fetch(window.routes.cart_url + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        var hasSelf = false;
        var hasGift = false;
        cartData.items.forEach(function (item) {
          var props = item.properties || {};
          if (props['Shipping To'] === 'Yourself') hasSelf = true;
          if (props['Shipping As Gift'] === 'Yes') hasGift = true;
        });
        var mixedActive = hasSelf && hasGift;
        if (!mixedActive) return { ok: true };

        var attrs = cartData.attributes || {};
        var missing = REQUIRED_FIELDS.filter(function (fieldName) {
          var key = ATTRIBUTE_KEY_BY_FIELD[fieldName];
          // whitespace-only counts as empty
          return !String(attrs[key] || '').trim();
        });

        var cfg = config();
        if (cfg.enabled) {
          var method = attrs['Gift Shipping Method'];
          if (method !== 'Standard' && method !== 'Priority') {
            missing.push('method');
          } else {
            var wantVariantId = method === 'Standard' ? cfg.standard.variantId : cfg.priority.variantId;
            var feePresent = cartData.items.some(function (item) {
              return String(item.variant_id) === String(wantVariantId) && item.quantity === 1;
            });
            if (!feePresent) missing.push('fee-product-missing');
          }
        }

        return { ok: missing.length === 0, missing: missing };
      })
      .catch(function () {
        // Network failure: fail closed (block) rather than silently
        // letting checkout through on an unverified cart state.
        return { ok: false, missing: ['network-error'] };
      });
  }

  // Press on a grayed-out checkout / express-checkout button (see
  // showBlockedCheckoutPrompt above). Works for mouse clicks and taps;
  // clientX/Y of 0,0 means a keyboard-generated click, which can't
  // target a disabled button anyway, so it's ignored.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.clientX && !event.clientY) return;
      if (!isMixedDestinationActiveOnPage()) return;
      // The checkout button itself now receives its own clicks (handled
      // by the checkout click listener below), so only fall back to
      // this position check for presses that land somewhere else, i.e.
      // on the express-checkout buttons (inert while blocked) or a
      // checkout button still rendered with `disabled`.
      if (event.target.closest && event.target.closest('[data-gift-shipping-checkout-button]')) return;
      if (!blockedCheckoutAt(event.clientX, event.clientY)) return;
      showBlockedCheckoutPrompt();
    },
    true
  );

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-gift-shipping-checkout-button]');
    if (!button) return;
    if (!isMixedDestinationActiveOnPage()) return;

    // Always intercept first — even if the on-screen form looks
    // complete, checkout only actually proceeds after validateCartState()
    // confirms the real cart agrees.
    event.preventDefault();

    var errors = validateAll();
    if (Object.keys(errors).length > 0) {
      renderAllErrors(errors);
      showGateMessages();
      focusFirstInvalid(errors);
      return;
    }

    button.disabled = true;
    var originalText = button.textContent;
    button.textContent = 'Checking…';

    validateCartState().then(function (result) {
      if (result.ok) {
        var form = document.getElementById(button.getAttribute('form'));
        if (!form) return;

        // Explicitly guarantee the "checkout" field is present in the
        // submission, rather than relying on requestSubmit(button) to
        // correctly carry the button's own name/value through — traced
        // this via the Network tab: the POST was landing, but its 302
        // response redirected back to /cart instead of /checkout,
        // which only happens when Shopify's backend doesn't see a
        // "checkout" field in the submitted data at all. This sidesteps
        // that regardless of why the submitter approach wasn't working.
        var checkoutField = form.querySelector('input[name="checkout"]');
        if (!checkoutField) {
          checkoutField = document.createElement('input');
          checkoutField.type = 'hidden';
          checkoutField.name = 'checkout';
          form.appendChild(checkoutField);
        }
        checkoutField.value = button.value || 'Checkout';

        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.submit();
        }
        return;
      }

      button.disabled = false;
      button.textContent = originalText;

      if (result.missing && result.missing.indexOf('fee-product-missing') !== -1) {
        // The chosen method's fee line isn't in the cart yet — most
        // likely reconcileFeeProduct() is still finishing. Kick it and
        // ask the customer to try again in a moment rather than
        // guessing at when it'll be done.
        reconcileFeeProduct();
        renderFieldError('method', 'Finishing setup for your gift shipping — please click Checkout again in a moment.');
        return;
      }
      if (result.missing && result.missing.indexOf('network-error') !== -1) {
        renderFieldError('method', 'Could not verify your gift shipping details — please try again.');
        return;
      }
      // Fell out of sync with what's actually saved (e.g. edited in
      // another tab) — re-render errors against the real missing fields.
      var freshErrors = {};
      (result.missing || []).forEach(function (fieldName) {
        freshErrors[fieldName] = validateField(fieldName) || 'This field needs to be completed.';
      });
      renderAllErrors(freshErrors);
      focusFirstInvalid(freshErrors);
    });
  });

  // Re-check the fee product any time the cart changes from ANY source
  // (quantity steppers, remove buttons, another tab, etc.) — catches the
  // case where removing the last gift or self item should also remove
  // the now-orphaned fee line, clear a gift note that's no longer
  // relevant, and clear a Gift Occasion selection whose Gift Box item is
  // no longer in the cart, automatically.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Every AJAX cart re-render (Dawn's own cart.js) replaces the
      // gift-message textarea(s) with fresh markup — a brand new
      // element the customer hasn't typed into yet, sized only by CSS
      // min-height. If it's carrying pre-filled content taller than
      // that (the default signature, or a longer note than the
      // min-height), it needs this to actually show all of it,
      // regardless of who/what triggered the re-render.
      autoGrowAllGiftMessages();
      updateAllGiftMessageClearButtons();
      // Re-rendered checkout markup arrives with Liquid's `disabled`;
      // convert it to the look-disabled-but-clickable state right away.
      updateCheckoutGatingFromLiveForm();
      if (event && event.source === 'gift-shipping') return; // avoid self-triggered loops
      reconcileFeeProduct(event && event.cartData);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    autoGrowAllGiftMessages();
    updateAllGiftMessageClearButtons();
    updateCheckoutGatingFromLiveForm();
    // Catches any case where the saved "Gift Shipping Method"
    // attribute doesn't match the actual fee line presence right on
    // load — e.g. a mutation that was interrupted before this fix
    // existed, or any other way the two could have drifted apart —
    // rather than only correcting it reactively the next time the
    // customer happens to touch something else in the cart.
    reconcileFeeProduct();
  });

  // Browsers can restore a page from the back-forward cache (bfcache)
  // when the customer clicks Back, instead of re-fetching it fresh.
  // That restored snapshot reflects whatever the DOM looked like at
  // the moment they navigated away — which can be mid-reconcile (e.g.
  // they deleted an item and clicked Checkout before the fee-line
  // removal had actually finished), showing stale content until a
  // manual reload. event.persisted is true specifically when the page
  // came from bfcache rather than a normal fresh load, so this only
  // re-checks in that case rather than running twice on every load.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      autoGrowAllGiftMessages();
      updateAllGiftMessageClearButtons();
      reconcileFeeProduct();
      updateCheckoutGatingFromLiveForm();
    }
  });

  /* Keeps focus in the textarea when the Clear button is clicked/
     tapped with a mouse or finger. Mousedown, not click, is what a
     browser normally uses to decide where focus goes next — by the
     time 'click' fires below, focus has usually already jumped to the
     button, blurring the textarea. Calling preventDefault() here
     stops that focus jump before it happens, so for a normal
     mouse/touch interaction the textarea is never blurred at all —
     none of the focusout/blur handling elsewhere in this file even
     runs. (A keyboard activation — tabbing to the button and pressing
     Enter/Space — never fires 'mousedown', so it's unaffected by this
     and is instead handled inside the click listener below.) */
  document.addEventListener('mousedown', function (event) {
    if (event.target.closest && event.target.closest('.cart-gift-message-clear')) {
      event.preventDefault();
    }
  });

  /* Clear pill (rendered by Liquid as a sibling of the textarea inside
     .cart-gift-message-box — see giftMessageClearButton() above):
     wipes the note back to genuinely empty — no lingering signature,
     no default text re-inserted — and puts the cursor at the very
     start, ready to type a fresh message from the top, all WITHOUT
     leaving the textarea. Only once the customer actually clicks away
     from a box left empty like this does it revert to showing the
     native placeholder ("Enjoy your Gift!" + the From/signature
     lines) — see the .cart-gift-message-input:focus::placeholder rule
     in component-gift-shipping.css, which is what keeps that
     placeholder invisible for as long as the box stays focused, empty
     or not. Delegated via 'click' on document for the same AJAX-
     re-render reason as every other listener in this file. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.cart-gift-message-clear');
    if (!button) return;
    event.preventDefault();
    var messageContainer = button.closest('.cart__gift-message');
    var textarea = messageContainer && messageContainer.querySelector('.cart-gift-message-input');
    if (!textarea) return;
    textarea.value = '';
    textarea.scrollTop = 0;
    autoGrowGiftMessage(textarea);
    updateGiftMessageClearButton(textarea);
    saveGiftMessage(textarea);
    // The mousedown guard above already kept focus in the textarea for
    // a normal mouse/touch click — this only needs to actually move
    // focus there for the keyboard-activation case (Enter/Space on the
    // button, which the mousedown guard never sees, so focus is still
    // sitting on the button at this point). The skip flag stops the
    // focusin handler above from re-inserting the default signature
    // into what's supposed to be a genuinely blank box.
    if (document.activeElement !== textarea) {
      textarea.dataset.skipDefaultInsert = 'true';
      textarea.focus();
    }
    textarea.setSelectionRange(0, 0);
  });
})();
